import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import {
    ColorBrandBlue1,
    ColorNeutralDarkGrey,
    ColorNeutralWhite,
    Spacing12,
    Spacing16,
    Spacing24,
    Spacing32
} from 'autogen/design-tokens/tokens';
import styled from 'styled-components';
import type { TypeArticleTemplateDetailed } from 'types/autogen/contentful';
import { breakpoint } from 'variables';
import TopicList from './components/TopicsList';
import RelatedArticlesList from './components/RelatedArticlesList';
import { useEffect, useId } from 'react';
import { Typography } from 'components/generic/typography';
import { percentage, rem } from 'modules/helpers/style';
import { useTranslation } from 'react-i18next';
import { MoreContent, OthersRead } from 'autogen/translation-keys/trans-website-articleblock';
import ShareArticleCard from './components/ShareCard';
import AuthorCard from './components/AuthorCard';
import { hideOnDesktop, hideOnMobile } from 'modules/helpers/mixins';
import BrandCard from './components/BrandCard';
import FlexColumn from 'components/Layout/components/Flex/components/FlexColumn';
import Layout from 'components/Layout';
import TemplateComponentRenderer from '../../shared/TemplateComponentRenderer';
import ArticleUniverseGlobalStyling from 'components/pages/ArticleUniverse/shared/ArticleUniverseGlobalStyling';
import TopAssetRenderer from './TopAssetRenderer';
import type { ContentfulArticleGroupModel } from 'autogen/swagger/all';
import { ReadMore } from 'autogen/translation-keys/trans-website-shared-buttons';
import LinkActionButton from 'components/Button/LinkActionButton';
import ArticleCardGridSection from 'components/pages/ArticleUniverse/components/ArticleCards/ArticleCardGridSection';
import SectionNavigationRenderer from 'components/pages/ArticleUniverse/components/SectionNavigation/SectionNavigationRenderer';
import useDefaultDataLayerTrackingEvent from 'modules/tracking/GA4/hooks/useSetupDefaultDataLayerTrackingEvent';

// #region Styles
const Wrapper = styled(Layout.Section)`
    ${breakpoint.up('1025px')} {
        display: grid;
        grid-template-columns: ${percentage(3, 12)} 1fr ${percentage(2, 12)};
        gap: ${Spacing32};
    }
`;

const AsideNav = styled.aside`
    ${hideOnMobile}
    min-width: 0px;

    ${breakpoint.up('1025px')} {
        display: flex;
        flex-direction: column;
        gap: ${Spacing32};
        align-self: start;
        background-color: ${ColorNeutralWhite};
        padding: ${Spacing24} ${Spacing16};
        border-top: 5px solid ${ColorNeutralDarkGrey};
    }
`;

const Article = styled.article`
    display: flex;
    flex-direction: column;
    gap: ${Spacing16};
    min-width: 0px;

    ${breakpoint.up('1025px')} {
        gap: ${Spacing24};
    }

    // remove padding for layouts
    & > section {
        padding-left: 0;
        padding-right: 0;
    }
`;

const Aside = styled.aside`
    ${hideOnMobile}
    min-width: 0px;

    ${breakpoint.up('1025px')} {
        display: flex;
        flex-direction: column;
        gap: ${Spacing32};
    }

`;

const Headline = styled(Typography.Headline)`
    color: ${ColorBrandBlue1};
    margin-top: 0;
    margin-bottom: 0;
`;

const SectionLabel = styled(Typography.Body)`
    display: block;
    text-transform: uppercase;
    letter-spacing: ${rem(1.4)};
    color: ${ColorNeutralDarkGrey};

    &:after {
        content: '';
        background-color: currentColor;
        display: block;
        height: ${rem(2)};
        width: ${rem(55)};
        margin-top: ${Spacing12};
    }
`;

const StyledBody = styled(Typography.Body)`
    margin-bottom: 0;
`;

const AsideNavWrapper = styled.nav`
    display: flex;
    flex-direction: column;
    gap: ${Spacing16};
`;

const ArticleInfo = styled(FlexColumn)`
    ${hideOnDesktop}
`;

const StyledArticleCardGridSection = styled(ArticleCardGridSection)`
    padding: 0;
`;

const StyledLinkActionButton = styled(LinkActionButton)`
    width: max-content;
    margin-inline: auto;
`;
// #endregion Styles

function renderReadMoreArticles(articleGroup?: ContentfulArticleGroupModel) {
    if (!articleGroup) return null;

    const length = articleGroup.articles.hits.length;

    if (!length) return null;
    const { t } = useTranslation();

    return (
        <Layout.Section>
            <Typography.Headline as="h2" style={{ marginTop: Spacing16 }} size="L" id="readMore">
                {t(ReadMore)}
            </Typography.Headline>
            <StyledArticleCardGridSection articleGroup={articleGroup} />
            {articleGroup.articles.paging.totalHits > length && articleGroup.seeMoreLink && (
                <StyledLinkActionButton variant="secondary" size="lg" action={articleGroup.seeMoreLink}>
                    {t(ReadMore)}
                </StyledLinkActionButton>
            )}
        </Layout.Section>
    );
}

export default function ArticleTemplate(props: TypeArticleTemplateDetailed): JSX.Element {
    const {
        fields,
        sys: { id, updatedAt }
    } = props;
    const inspectorProps = useContentfulInspectorMode({ entryId: id });
    const {
        author,
        imageRatio169,
        section,
        headline,
        content,
        relatedArticles,
        teaser,
        topics,
        brand,
        publishDate,
        sectionMenuData
    } = fields;
    const topicsLabelId = useId();
    const relatedArticlesId = useId();
    const { t } = useTranslation();
    const defaultDataLayerTrackingEvent = useDefaultDataLayerTrackingEvent({ pageType: 'Article' });

    useEffect(() => {
        defaultDataLayerTrackingEvent();
    }, [defaultDataLayerTrackingEvent]);

    const readMoreArticles = fields.readMoreArticles;

    return (
        <>
            {sectionMenuData && (
                <SectionNavigationRenderer
                    sections={sectionMenuData?.sections}
                    currentSectionId={sectionMenuData.sectionEntryId}
                    frontPageUrl={sectionMenuData?.articleFrontPage}
                />
            )}
            <ArticleUniverseGlobalStyling />

            <Layout.Page>
                <Wrapper
                    contentRenderer={(children) => <>{children}</>}
                    {...inspectorProps({ fieldId: 'internalName' })}
                >
                    <AsideNav>
                        {topics && topics?.length > 0 && (
                            <AsideNavWrapper aria-labelledby={topicsLabelId}>
                                <Typography.Headline size="S" id={topicsLabelId}>
                                    {t(MoreContent)}
                                </Typography.Headline>
                                <TopicList topics={topics} />
                            </AsideNavWrapper>
                        )}

                        {relatedArticles && (
                            <AsideNavWrapper aria-labelledby={relatedArticlesId}>
                                <Typography.Headline size="S" id={relatedArticlesId}>
                                    {t(OthersRead)}
                                </Typography.Headline>
                                <RelatedArticlesList relatedArticles={relatedArticles} />
                            </AsideNavWrapper>
                        )}
                    </AsideNav>

                    <Article>
                        {section?.fields.publicName && (
                            <SectionLabel size="M" strong forwardedAs="div">
                                {section.fields.publicName}
                            </SectionLabel>
                        )}
                        {headline && (
                            <Headline as="h1" size="L">
                                {headline}
                            </Headline>
                        )}
                        {teaser && (
                            <StyledBody size="L" strong>
                                {teaser}
                            </StyledBody>
                        )}
                        {imageRatio169 && <TopAssetRenderer {...imageRatio169} />}
                        {(brand || author) && (
                            <ArticleInfo gap={Spacing16} aria-hidden="true">
                                {brand && <BrandCard brand={brand} />}
                                {author && <AuthorCard publishDate={publishDate || updatedAt} {...author} />}
                            </ArticleInfo>
                        )}

                        {content &&
                            content.length > 0 &&
                            content.map((data) => <TemplateComponentRenderer key={data?.sys.id} {...data} />)}
                    </Article>

                    <Aside>
                        {brand && <BrandCard brand={brand} />}
                        <ShareArticleCard />
                        {author && <AuthorCard publishDate={publishDate || updatedAt} {...author} />}
                    </Aside>
                </Wrapper>
                {renderReadMoreArticles(readMoreArticles)}
            </Layout.Page>
        </>
    );
}
